import {
    IonGrid,
    IonCol,
    IonRow,
    IonContent,
    IonHeader,
    IonIcon,
    IonText,
    IonPage,
    IonList,
    IonItem,
    IonRange,
    IonLabel,
    IonToggle,
    useIonViewWillEnter,
    IonToast,
    isPlatform,
    IonFooter
  } from '@ionic/react';
import { volumeOff, shuffle, moon, hourglass, settings } from 'ionicons/icons';
import { ReactComponent as PrayerIcon } from '../../assets/images/prayerLogo.svg';
import timerOutlineMinus from '../../assets/images/timer-outline-minus.svg';
import timerOutlinePlus from '../../assets/images/timer-outline-plus.svg';
import './PrayerSessionSettingsPage.scss';
import React, { useState, useEffect, useRef, useContext } from 'react';
import PageHeader from '../../components/PageHeader/PageHeader';
import IRouteInfo from '../../interfaces/IRouteInfo';
import { AppRouteInfo, AppRouteKeys } from '../../routes/AppRoutes';
import { useHistory } from 'react-router-dom';
import IPrayerSettings from '../../interfaces/IPrayerSettings';
import PrayerSettingsService from '../../services/PrayerSettingsService';
import NoSleepContext from '../../components/NoSleepContext/NoSleepContext';
import { DefaultPrayerSettings } from '../../data/DefaultPrayerSettings';
import { InsomniaOriginal } from '@awesome-cordova-plugins/insomnia';
import NoSleep from 'nosleep.js';
  
  const Prayer: React.FC<IRouteInfo> = (props) => {

    const history = useHistory();
    const [timedSetting, setTimedSetting] = useState(DefaultPrayerSettings.timed);
    const [minPrayerTimeSetting, setMinPrayerTimeSetting] = useState(DefaultPrayerSettings.minimumPrayerTime);
    const [sessionTimeSetting, setSessionTimeSetting] = useState(DefaultPrayerSettings.sessionTime);
    const [shuffleSetting, setShuffleSetting] = useState(DefaultPrayerSettings.shuffle);
    const [alertSetting, setAlertSetting] = useState(DefaultPrayerSettings.alert);
    const [showSettings, setShowSettings] = useState(DefaultPrayerSettings.showSettings);
    const [nightModeSettings, setNightModeSettings] = useState(DefaultPrayerSettings.nightMode);
    const [hapticAlertSetting, setHapticAlertSettings] = useState(DefaultPrayerSettings.hapticAlert);
    const lastStoredPrayerSettings = useRef<IPrayerSettings | undefined>(undefined);
    const [displayToastFailed, setDisplayToastFailed] = useState(false);
    const noSleep = useContext(NoSleepContext);

    useIonViewWillEnter(() => {
      PrayerSettingsService.getPrayerSettings().then(response => {
        if(response.Success) {
          lastStoredPrayerSettings.current = response.Data!
          setTimedSetting(!!response.Data?.timed);
          setMinPrayerTimeSetting(response.Data?.minimumPrayerTime!);
          setSessionTimeSetting(response.Data?.sessionTime!);
          setShuffleSetting(!!response.Data?.shuffle);
          setAlertSetting(!!response.Data?.alert);
          setShowSettings(!!response.Data?.showSettings);
          setNightModeSettings(!!response.Data?.nightMode);
          setHapticAlertSettings(!!response.Data?.hapticAlert)
        } else {
          PrayerSettingsService.createPrayerSettings(DefaultPrayerSettings).then(response => {
            if(response.Success) {
              lastStoredPrayerSettings.current = response.Data;
            } else {
              setDisplayToastFailed(true);
              console.log('Failed to Add initial settings on load');        
            }
          });
        }
      });
    });

    useEffect(() => {
      if(lastStoredPrayerSettings.current) {
        lastStoredPrayerSettings.current = { 
          id: 0,
          timed: timedSetting,
          minimumPrayerTime: minPrayerTimeSetting,
          sessionTime: sessionTimeSetting,
          shuffle: shuffleSetting,
          alert: alertSetting,
          showSettings: showSettings,
          nightMode: nightModeSettings,
          hapticAlert: hapticAlertSetting,
        }
        console.log('Updating prayer settings');
        PrayerSettingsService.updatePrayerSettings(lastStoredPrayerSettings.current).then(response => {
          if(!response.Success) {
            setDisplayToastFailed(true);
            console.log('Failed to update settings');
          }
        });
      }
      return () => {}
    }, [ 
      timedSetting, 
      minPrayerTimeSetting, 
      sessionTimeSetting,
      shuffleSetting,
      alertSetting,
      showSettings,
      nightModeSettings,
      hapticAlertSetting,
    ])

    const displayPrayerTime = (time: number) => {
      return time > 60 ?
        <div className="prayer-setting-range-value"><span>{(time/60).toFixed(2)}</span>min</div> :
        <div className="prayer-setting-range-value"><span>{time}</span>sec</div>
    }

    const enableNoSleep = () => {
      if(isPlatform('hybrid')) {
        const awake = noSleep as InsomniaOriginal;
        awake?.keepAwake();
      } else if (!isPlatform('ios')) {
        const awake = noSleep as NoSleep;
        awake?.enable();
      } 
    }

    return (
      <IonPage>
        <IonHeader>
        <PageHeader showBackButton={true} {...props}></PageHeader>
        </IonHeader>
        <IonContent color="prayer-shade">
          <IonGrid fixed={true}>
            <IonRow className="ion-text-center">
              <IonCol>
                <IonList className="prayer-settings-list">
                  <IonItem>
                    <IonLabel className="prayer-settings-label">
                      <IonIcon icon={settings} className="prayer-settings-icon"/>
                      ALWAYS SHOW SETTINGS
                    </IonLabel>
                    <IonToggle color="secondary" slot="end" checked={!!showSettings} onIonChange={e => setShowSettings(e.detail.checked)} />
                  </IonItem>
                  <IonItem>
                    <IonLabel className="prayer-settings-label">
                      <IonIcon icon={moon} className="prayer-settings-icon"/>
                      PRAYER NIGHT MODE
                    </IonLabel>
                    <IonToggle color="secondary" slot="end" checked={!!nightModeSettings} onIonChange={e => setNightModeSettings(e.detail.checked)} />
                  </IonItem>
                  <IonItem>
                    <IonLabel className="prayer-settings-label">
                      <IonIcon icon={shuffle} className="prayer-settings-icon"/>
                      SHUFFLE PRAYER LIST
                    </IonLabel>
                    <IonToggle color="secondary" slot="end" checked={!!shuffleSetting} onIonChange={e => setShuffleSetting(e.detail.checked)} />
                  </IonItem>
                  <IonItem lines={timedSetting ? 'full' : 'none'}>
                    <IonLabel className="prayer-settings-label">
                      <IonIcon icon={hourglass} className="prayer-settings-icon"/>
                      ENABLE TIMER
                    </IonLabel>
                    <IonToggle color="secondary" slot="end" checked={!!timedSetting} onIonChange={e => setTimedSetting(e.detail.checked)} />
                  </IonItem>
                  { timedSetting ? 
                  <div>
                    <div className="prayer-settings-custom-item">
                      <div className="block">
                      <div className="prayer-settings-range-label">
                        <div className="prayer-setting-range-text">Total Prayer Session Time: </div>
                        <div className="prayer-setting-range-value"><span>{sessionTimeSetting}</span>min</div>
                      </div>
                      </div>
                      <div className="block">
                      <IonRange pin={true} value={sessionTimeSetting} className="prayer-settings-timer-range"
                        onIonChange={e => setSessionTimeSetting(e.detail.value as number)}  
                        min={1} max={120} color="secondary">
                        <IonIcon size="large" slot="start" color="secondary" src="./assets/appIcons/timer-outline-minus.svg" onClick={()=>{setSessionTimeSetting(sessionTimeSetting-1)}} />
                        <IonIcon size="large" slot="end" color="secondary" src="./assets/appIcons/timer-outline-plus.svg" onClick={()=>{setSessionTimeSetting(sessionTimeSetting+1)}}  />
                      </IonRange> 
                      </div>
                    </div>
                    <div className="prayer-settings-custom-item">
                      <div className="block">
                      <div className="prayer-settings-range-label">
                        <div className="prayer-setting-range-text">Minimum Time for Each Prayer: </div>
                        {displayPrayerTime(minPrayerTimeSetting)}
                      </div>
                      </div>
                      <div className="block">
                      <IonRange pin={true} value={minPrayerTimeSetting} className="prayer-settings-timer-range"
                        onIonChange={e => setMinPrayerTimeSetting(e.detail.value as number)}  
                        min={5} max={300} color="secondary">
                        <IonIcon size="large" slot="start" color="secondary" src={timerOutlineMinus} onClick={()=>{setMinPrayerTimeSetting(minPrayerTimeSetting-1)}} />
                        <IonIcon size="large" slot="end" color="secondary" src={timerOutlinePlus} onClick={()=>{setMinPrayerTimeSetting(minPrayerTimeSetting+1)}}  />
                      </IonRange> 
                      </div>
                    </div>
                    <IonItem lines="none">
                      <IonLabel className="prayer-settings-label">
                        <IonIcon icon={volumeOff} className="prayer-settings-icon"/>
                        TRANSITION SOUND
                      </IonLabel>
                      <IonToggle color="secondary" slot="end" checked={!!alertSetting} onIonChange={e => setAlertSetting(e.detail.checked)} />
                    </IonItem>
                    { (!(isPlatform('desktop') && isPlatform('tablet') && !isPlatform('hybrid')) && !(isPlatform('ios') && isPlatform('mobileweb'))) &&
                      <IonItem lines="none">
                        <IonLabel className="prayer-settings-label">
                          <IonIcon icon={volumeOff} className="prayer-settings-icon"/>
                          TRANSITION VIBRATION
                        </IonLabel>
                        <IonToggle color="secondary" slot="end" checked={!!hapticAlertSetting} onIonChange={e => setHapticAlertSettings(e.detail.checked)} />
                      </IonItem>
                    }
                  </div> : ''
                  }
                </IonList>
              </IonCol>
            </IonRow>
            <IonRow className="ion-text-center">
              <IonCol>
                <PrayerIcon className="color-secondary medium-size-prayer-button icon-glow"
                  onClick={() => {
                    enableNoSleep();
                    history.push(AppRouteInfo[AppRouteKeys.PrayNow].path, {direction: 'forward'});
                  }}></PrayerIcon>
                <IonText color="secondary" className="prayer-button-label">PRAY NOW</IonText>
              </IonCol>
            </IonRow>
          </IonGrid>
          <IonToast color="danger"
            isOpen={displayToastFailed}
            onDidDismiss={() => setDisplayToastFailed(false)}
            message="Error: Failed to Update Prayer Settings"
            duration={2500} />
        </IonContent>
        <IonFooter>
          <div className="prayer-settings-ios-safe-zone"></div>
        </IonFooter>
      </IonPage>
    );
  };
  
  export default Prayer;
  